import { MergeDeep } from "type-fest";
import { Database as DatabaseGenerated } from "./supabase";
import { NearbyResult, NearbyType } from "./nearby";
import { Estate, VitecEstateType } from "./vitec";
import { CategoryType, StatusType } from "./homes";
import { FeatureType } from "./filters";
import { SellerTimeline } from "./intents";
import { BuyerIntentType, BuyerStatusType, CountResult, GeoJSONType } from ".";
import { Provider } from "@supabase/supabase-js";
import { AgencySettings } from "./agencies";
import { EKOPOST } from "./ekopost";

export type ImageSourceType = "user" | "agency" | "vitec" | "streetview";

export type ProviderType = Database["public"]["Enums"]["provider"];
export type AppMode = Database["public"]["Enums"]["app_mode"];
export type UserEvent = Database["public"]["Enums"]["user_event"];
export type InterestLevel = Database["public"]["Enums"]["interest_level"];
export type OwnershipType = Database["public"]["Enums"]["ownership_type"];

// Override the type for a specific column in a view:
export type Database = MergeDeep<
  DatabaseGenerated,
  {
    public: {
      Tables: {
        images: {
          Row: {
            source: ImageSourceType;
          };
          Insert: {
            source: ImageSourceType;
          };
        };
        addresses: {
          Row: {
            category: CategoryType;
            area?: NestedArea | null;
          };
          Insert: {
            category: CategoryType;
            municipality_id?: number | null;
          };
        };
        letters: {
          Row: {
            sent_via: (typeof EKOPOST.TYPES)[keyof typeof EKOPOST.TYPES];
          };
        };
        homes: {
          Row: {
            // created_at: Date;
            // updated_at?: Date | null;
            nearby_updated_at?: Date | null;
            features_completed_at?: Date | null;
            features?: FeatureType[] | null;
            intent?: SellerTimeline | null;
            provider: ProviderType | null;
            provider_data?: Estate | null;
            provider_type?: VitecEstateType | null;
            nearby_json?: NearbyResult[] | null;
            area?: NestedArea | null;
          };
          Insert: {
            // created_at: Date;
            // updated_at?: Date | null;
            nearby_updated_at?: Date | null;
            features_completed_at?: Date | null;
            features?: FeatureType[] | null;
            intent?: SellerTimeline | null;
            provider: ProviderType | null;
            provider_data?: string | null;
            provider_type?: VitecEstateType | null;
            nearby_json?: string | null;
          };
        };
        users: {
          Row: {
            // created_at: Date;
            // updated_at: Date | null;
            buyer_status: BuyerStatusType | null;
            buyer_intent: BuyerIntentType | null;
            buyer_persona: string | null;
            is_matching_enabled: boolean | null;
          };
          Insert: {
            // created_at: Date;
            // updated_at: Date | null;
            buyer_status: BuyerStatusType | null;
            buyer_intent: BuyerIntentType | null;
            buyer_persona: string | null;
            is_matching_enabled?: boolean | null;
          };
          Update: {
            is_matching_enabled?: boolean | null;
          };
        };
        searches: {
          Row: {
            categories: CategoryType[] | null;
            statuses: StatusType[] | null;
            features: FeatureType[] | null;
            nearby: NearbyType[] | null;
            geom: GeoJSONType | null;
            status: BuyerStatusType | null;
            intent: BuyerIntentType | null;
            location: GeoJSONType | null;
          };
          Insert: {
            categories: CategoryType[] | null;
            statuses: StatusType[] | null;
            features: FeatureType[] | null;
            nearby: NearbyType[] | null;
            geom: GeoJSONType | null;
            status: BuyerStatusType | null;
            intent: BuyerIntentType | null;
            location: GeoJSONType | null;
          };
        };
        agencies: {
          Row: {
            auth_providers: Provider[];
            settings: AgencySettings;
            default_search_count?: CountResult;
          };
          Insert: {
            auth_providers: Provider[];
            settings: AgencySettings;
            default_search_count?: CountResult;
          };
        };
        providers: {
          Row: {
            name: ProviderType;
          };
          Insert: {
            name: ProviderType;
          };
        };
      };
    };
  }
>;

export type NestedViewing = Database["public"]["Tables"]["viewings"]["Row"] & {
  viewing_slots?: Array<
    Database["public"]["Tables"]["viewing_slots"]["Row"] & {
      viewing_participants?: Database["public"]["Tables"]["viewing_participants"]["Row"][];
    }
  >;
};

export type NestedProjectViewing = NestedViewing & {
  project_id?: number | null;
  home?: {
    id: number;
    address: {
      id: number;
      street: string;
      apartment_number: string | null;
      city: string | null;
      rooms: number | null;
      living_space: number | null;
      additional_space: number | null;
      plot_size: number | null;
      monthly_fee: number | null;
      category: Database["public"]["Enums"]["category"] | null;
    } | null;
    images: Array<{
      id: number;
      cloudflare_image_id: string | null;
      provider_sort_order: number | null;
    }>;
  } | null;
};

export type NestedHome = Database["public"]["Tables"]["homes"]["Row"] & {
  address: Database["public"]["Tables"]["addresses"]["Row"];
  status: Database["public"]["Tables"]["statuses"]["Row"];
  images: Database["public"]["Tables"]["images"]["Row"][];
  viewings?: NestedViewing[];
  // vitec_estates: VitecEstateWithBrokers[] | null;
  // vitec_estate: VitecEstateWithBrokers | null;
  sellers?: Database["public"]["Tables"]["users"]["Row"][];
  responsible_broker?: Database["public"]["Tables"]["brokers"]["Row"] | null;
  additional_contact?: Database["public"]["Tables"]["brokers"]["Row"] | null;
  valuations?: Database["public"]["Tables"]["valuations"]["Row"][];
  count?: number; // Used to get total count in neondatabase query
  loading?: boolean; // Used to show skeleton for the card
};

/**
 * Minimal home type for HomeCard list views.
 * Contains only the fields needed for rendering home cards.
 * Derived from database types using Pick.
 */
export type HomeCardHome = Pick<
  Database["public"]["Tables"]["homes"]["Row"],
  "id" | "title" | "price" | "price_type" | "estimate" | "is_bidding_active"
> & {
  address: Pick<
    Database["public"]["Tables"]["addresses"]["Row"],
    | "id"
    | "street"
    | "city"
    | "municipality"
    | "category"
    | "living_space"
    | "additional_space"
    | "rooms"
    | "monthly_fee"
    | "plot_size"
    | "location"
  > & {
    area?: Pick<Database["public"]["Tables"]["areas"]["Row"], "id" | "name"> | null;
  };
  status: Pick<
    Database["public"]["Tables"]["statuses"]["Row"],
    | "id"
    | "name"
    | "title"
    | "color_hex"
    | "show_publicly"
    | "show_price"
    | "show_address"
    | "show_status_badge"
    | "show_public_preview"
    | "show_verified_badge"
  >;
  images: (Pick<
    Database["public"]["Tables"]["images"]["Row"],
    | "id"
    | "cloudflare_image_id"
    | "name"
    | "home_id"
    | "is_hidden"
    | "uploaded"
    | "extension"
  > & {
    is_primary: boolean;
  })[];
  viewings?: Pick<
    Database["public"]["Tables"]["viewings"]["Row"],
    "id" | "start_time" | "end_time"
  >[];
  responsible_broker?: Pick<
    Database["public"]["Tables"]["brokers"]["Row"],
    "id" | "name" | "email" | "phone"
  > | null;
  additional_contact?: Pick<
    Database["public"]["Tables"]["brokers"]["Row"],
    "id" | "name" | "email" | "phone"
  > | null;
};

export type HomePreferences = {
  grid: number;
};

/** Lightweight shape returned by the map-specific homes query */
export type MapHomeLocation = {
  id: number;
  latitude: number;
  longitude: number;
  category: string;
  color_hex: string;
};

export type MapHome = Database["public"]["Tables"]["homes"]["Row"] & {
  id: number;
  category: CategoryType;
  status: Database["public"]["Tables"]["statuses"]["Row"];
  address: Database["public"]["Tables"]["addresses"]["Row"];
  area_geometry: string;
  municipality: Database["public"]["Tables"]["municipalities"]["Row"];
  images?: Database["public"]["Tables"]["images"]["Row"][]; // A single image is returned if includeFirstImage is true
};

export type InsertHome = Database["public"]["Tables"]["homes"]["Insert"];
export type InsertAddress = Database["public"]["Tables"]["addresses"]["Insert"];

export type ImageType = Database["public"]["Tables"]["images"]["Row"];

export type ProjectImageType =
  Database["public"]["Tables"]["project_images"]["Row"];

type BrokerNeonDbQueryFields = {
  brokered_homes_count?: number;
  count?: number; // Used to get total count in neondatabase query
  loading?: boolean; // Used to show skeleton for the card
  homes?: NestedHome[];
  office?: {
    id: number;
    city: string;
    customer_id: string;
    provider_id: string;
  };
  areas?: (Database["public"]["Tables"]["areas"]["Row"] & {
    municipality?: Pick<
      Database["public"]["Tables"]["municipalities"]["Row"],
      "id" | "name"
    >;
  })[];
  municipalities?: Pick<
    Database["public"]["Tables"]["municipalities"]["Row"],
    "id" | "name"
  >[];
  municipality?: Pick<
    Database["public"]["Tables"]["municipalities"]["Row"],
    "id" | "name"
  > | null;
  active_homes_count?: number;
  sold_homes_count?: number;
  recommendations?: Database["public"]["Tables"]["broker_recommendations"]["Row"][];
};

export type Broker = Database["public"]["Tables"]["brokers"]["Row"] &
  BrokerNeonDbQueryFields;

// Minimal broker data for use in lists
export type BrokerOverview = Pick<
  Database["public"]["Tables"]["brokers"]["Row"],
  | "id"
  | "name"
  | "title"
  | "rating"
  | "rating_count"
  | "rating_link"
  | "phone"
  | "email"
> &
  BrokerNeonDbQueryFields;

export type InsertBroker = Database["public"]["Tables"]["brokers"]["Insert"];

export type NestedAgency = Database["public"]["Tables"]["agencies"]["Row"] & {
  statuses: Database["public"]["Tables"]["statuses"]["Row"][];
  default_status?: Database["public"]["Tables"]["statuses"]["Row"];
  providers?: NestedProvider[];
  theme?: Database["public"]["Tables"]["themes"]["Row"];
};

export type SpeculatorArea = {
  id: number;
  name: string;
  has_image: boolean;
  municipality: NestedMunicipality;
};

export type PublicSpeculatorUser = Pick<
  Database["public"]["Tables"]["users"]["Row"],
  "id" | "avatar_url"
> & {
  name: string | null;
  first_name: string | null;
  last_name: null;
  email: "";
  phone: null;
  /** Discriminator to identify anonymized public users */
  _isPublic: true;
};

/**
 * @deprecated
 * use NestedSpeculator instead
 */
export type NestedBuyer = Database["public"]["Tables"]["users"]["Row"] & {
  search: NestedSearch;
  areas?: SpeculatorArea[];
  count?: number;
  user_count?: number;
  bid_count?: number;
  viewing_count?: number;
  loading?: boolean; // Used to show skeleton for the card
};

/**
 * @deprecated
 * use PublicNestedSpeculator instead
 */
export type PublicNestedBuyer = Omit<
  NestedBuyer,
  keyof Database["public"]["Tables"]["users"]["Row"]
> &
  PublicSpeculatorUser;

/**
 * Union type for any user-like object that has name fields.
 * Works with both full user objects and anonymized public versions.
 */
export type UserLike =
  | NestedUser
  | NestedBuyer
  | PublicSpeculatorUser
  | PublicNestedBuyer;

// FIXME: All searches go inside, not one-user-per-search
export type NestedSpeculator = Database["public"]["Tables"]["users"]["Row"] & {
  search: NestedSearch;
  areas?: SpeculatorArea[];
  match_score: number;
  score_recency?: number;
  score_activity?: number;
  score_area?: number;
  score_specificity?: number;
  score_overlap?: number;
  count?: number;
  user_count?: number;
  bid_count?: number;
  viewing_count?: number;
  loading?: boolean; // Used to show skeleton for the card
};

export type PublicNestedSpeculator = Omit<
  NestedSpeculator,
  keyof Database["public"]["Tables"]["users"]["Row"]
> &
  PublicSpeculatorUser;

/**
 * Type guard to check if a buyer is a public/anonymized version.
 */
export const isPublicSpeculator = (
  speculator: NestedSpeculator | PublicNestedSpeculator
): speculator is PublicNestedSpeculator => {
  return "_isPublic" in speculator && speculator._isPublic === true;
};


export type NestedUser = Database["public"]["Tables"]["users"]["Row"] & {
  homes?: NestedHome[];
  homes_count?: number;
  searches?: NestedSearch[];
  user_searches_count?: number;
  broker?: Broker;
  interests?: NestedInterest[];
  project_interests?: NestedProjectInterest[];
  seller_records?: NestedSeller[];
  // Roles from user_roles relation
  roles?: string[];
  // Permissions from JWT claims for fine-grained RBAC
  permissions?: Record<string, string[]>;
};

export type NestedStatus = Database["public"]["Tables"]["statuses"]["Row"];

export type NestedAddress = Database["public"]["Tables"]["addresses"]["Row"];

export type NestedAutocompleteAddress =
  Database["public"]["Tables"]["addresses_autocomplete"]["Row"];

export type NestedLetter = Database["public"]["Tables"]["letters"]["Row"];

export type SearchArea = Database["public"]["Tables"]["areas"]["Row"];
export type Municipality =
  Database["public"]["Tables"]["municipalities"]["Row"];

export type Search = Database["public"]["Tables"]["searches"]["Row"];

export type NestedSearch = Database["public"]["Tables"]["searches"]["Row"] & {
  // For homes, brokers, projects, offices
  areaIds?: number[];
  municipalityIds?: number[];
  officeIds?: number[];
  brokerIds?: number[];
  projectIds?: number[];
  streetIds?: number[];
  excludeAssistants?: boolean;
  // For broker search: if true, only return brokers whose focus area contains the point (with fallback)
  // If false/undefined, return all brokers sorted by distance
  requireContainingArea?: boolean;
  user?: NestedUser;
  homes: NestedHome[];
  // For speculators
  interests?: InterestLevel[];
};

export type NestedSearchWithObjects = NestedSearch & {
  areas?: SearchArea[];
  municipalities?: Municipality[];
};

export type NestedJob = Database["public"]["Tables"]["jobs"]["Row"];

export type NestedProvider = Database["public"]["Tables"]["providers"]["Row"];

export type NestedArea = Database["public"]["Tables"]["areas"]["Row"] & {
  geometry?: string;
  municipality_name?: string; // TODO: Deprecate
  municipality_location?: string;
  municipality?: NestedMunicipality;
  brokers_count?: number;
  projects_count?: number;
  homes?: NestedHome[];
  brokers?: Broker[];
  place?: Place;
  rating?: number | null;
  rating_count?: number | null;
  rating_link?: string | null;
  rating_source?: string | null;
  rating_updated_at?: string | null;
};

export type NestedSeller = Database["public"]["Tables"]["sellers"]["Row"];


export type NestedInterest =
  Database["public"]["Tables"]["interests"]["Row"] & {
    home: NestedHome;
    buyer?: Database["public"]["Tables"]["users"]["Row"];
  };

export type NestedProjectInterest =
  Database["public"]["Tables"]["project_interests"]["Row"] & {
    project: NestedProject;
    buyer?: Database["public"]["Tables"]["users"]["Row"];
  };

type HomeWithImage = {
  id: number;
  image: {
    id: number;
    home_id: number;
    extension: string;
  };
};

export type NestedMunicipality =
  Database["public"]["Tables"]["municipalities"]["Row"] & {
    areas_count?: number;
    brokers_count?: number;
    projects_count?: number;
    brokers?: Broker[];
    areas?: NestedArea[];
    homes?: HomeWithImage[];
    place?: Place;
    loading?: boolean; // Used to show skeleton for the card
  };

export type Place = Database["public"]["Tables"]["places"]["Row"];

export type Project = Database["public"]["Tables"]["projects"]["Row"];

export type MapProject = Pick<
  Project,
  | "id"
  | "name"
  | "latitude"
  | "longitude"
  | "price_min"
  | "price_max"
  | "total_homes"
  | "is_bidding_active"
> & {
  status: Database["public"]["Tables"]["statuses"]["Row"];
  area_geometry?: string;
  municipality?: Database["public"]["Tables"]["municipalities"]["Row"] & {
    geometry?: string;
  };
  images?: ProjectImageType[];
};

export type NestedProject = Project & {
  status?: Database["public"]["Tables"]["statuses"]["Row"];
  images?: ProjectImageType[];
  area?: Database["public"]["Tables"]["areas"]["Row"] & {
    municipality?: Database["public"]["Tables"]["municipalities"]["Row"];
  };
  broker?: Database["public"]["Tables"]["brokers"]["Row"];
  additional_contact?: Database["public"]["Tables"]["brokers"]["Row"];
  developer?: Database["public"]["Tables"]["developers"]["Row"];
  homes?: NestedHome[];
  project_viewings?: NestedProjectViewing[];
  count?: number; // Used to get total count in neondatabase query
  loading?: boolean; // Used to show skeleton for the card
};

export type Developer = Database["public"]["Tables"]["developers"]["Row"];

// Use the unified user_event enum from database
export type UserAction = Database["public"]["Enums"]["user_event"];

// Lead sources
export type LeadSource = Database["public"]["Tables"]["lead_sources"]["Row"];
export type LeadSourceInsert = Database["public"]["Tables"]["lead_sources"]["Insert"];
